import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DowngradeAffectedEmployee } from '../downgrade-validation-dialog/downgrade-validation-dialog.component';
import { ProjectService } from 'src/app/Services/project.service';

import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AvatarModule } from 'ngx-avatars';

export interface ManagerToEmployeeDialogData {
  employees: DowngradeAffectedEmployee[];
}

type DialogStep = 'confirm' | 'processing' | 'result';

@Component({
  selector: 'app-manager-to-employee-dialog',
  templateUrl: './manager-to-employee-dialog.component.html',
  styleUrls: ['./manager-to-employee-dialog.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    AvatarModule
  ]
})
export class ManagerToEmployeeDialogComponent implements OnInit {
  step: DialogStep = 'confirm';
  errorMessage = '';
  selectedEmployees = new Set<string>();

  constructor(
    public dialogRef: MatDialogRef<ManagerToEmployeeDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: ManagerToEmployeeDialogData,
    private projectService: ProjectService
  ) { }

  ngOnInit(): void {
    if (this.data.employees) {
      this.data.employees.forEach(emp => {
        if (emp.id) {
          this.selectedEmployees.add(String(emp.id));
        }
      });
    }
  }

  get employeeCount(): number {
    return this.data.employees ? this.data.employees.length : 0;
  }

  get selectedCount(): number {
    return this.selectedEmployees.size;
  }

  get allSelected(): boolean {
    return this.selectedCount > 0 && this.selectedCount === this.employeeCount;
  }

  get selectedEmployeesList(): DowngradeAffectedEmployee[] {
    return this.data.employees ? this.data.employees.filter(emp => this.isEmployeeSelected(emp.id)) : [];
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedEmployees.clear();
    } else if (this.data.employees) {
      this.data.employees.forEach(emp => {
        if (emp.id) {
          this.selectedEmployees.add(String(emp.id));
        }
      });
    }
  }

  toggleEmployeeSelection(empId?: number | string): void {
    if (!empId) return;
    const idStr = String(empId);
    if (this.selectedEmployees.has(idStr)) {
      this.selectedEmployees.delete(idStr);
    } else {
      this.selectedEmployees.add(idStr);
    }
  }

  isEmployeeSelected(empId?: number | string): boolean {
    return !!empId && this.selectedEmployees.has(String(empId));
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    if (this.selectedCount === 0) {
      this.errorMessage = 'Please select at least one employee.';
      return;
    }

    this.step = 'processing';
    this.errorMessage = '';

    const updateRequests = this.selectedEmployeesList.map(emp => {
      const parts = (emp.name || '').trim().split(' ');
      const first_name = parts[0] || '';
      const last_name = parts.slice(1).join(' ') || '';
      return this.projectService.editEmployee(emp.id, { 
        roles: 'employee',
        first_name: first_name,
        last_name: last_name
      });
    });

    this.projectService.getTeamList().pipe(
      switchMap((teams: any) => {
        const teamUpdates: any[] = [];
        const selectedIds = Array.from(this.selectedEmployees);
        const teamsArray = teams || [];

        for (const team of teamsArray) {
          if (!team || !team.meta) continue;
          
          let modified = false;
          
          const managers = (team.meta.team_manager || '').split('~').filter(Boolean);
          const remainingManagers = managers.filter((m: string) => !selectedIds.includes(m));
          if (managers.length !== remainingManagers.length) {
            modified = true;
          }
          const formatedManager = remainingManagers.length ? '~' + remainingManagers.join('~') + '~' : '';
          
          const leads = (team.meta.team_lead || '').split('~').filter(Boolean);
          const remainingLeads = leads.filter((l: string) => !selectedIds.includes(l));
          if (leads.length !== remainingLeads.length) {
            modified = true;
          }
          const formatedLead = remainingLeads.length ? '~' + remainingLeads.join('~') + '~' : '';
          
          if (modified) {
            const teamData = {
              title: team.title?.rendered || team.title,
              slug: team.slug,
              meta: {
                team_manager: formatedManager,
                team_lead: formatedLead,
                team_developer: team.meta.team_developer || ''
              }
            };
            teamUpdates.push(this.projectService.editTeamMeamber(team.id, teamData));
          }
        }
        
        const allUpdates = [...updateRequests, ...teamUpdates];
        return forkJoin(allUpdates.length ? allUpdates : [of(true)]);
      })
    ).subscribe({
      next: () => {
        this.step = 'result';
      },
      error: (err) => {
        this.step = 'confirm';
        this.errorMessage = err?.error?.message || err?.message || 'Failed to update employee roles and teams. Please try again.';
      }
    });
  }

  onDone(): void {
    this.dialogRef.close(true);
  }

  getEmployeeTooltip(employee: DowngradeAffectedEmployee): string {
    return employee.role || employee.email?.trim() || employee.name;
  }
}
